import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { resizersReady, buildableScale } from './resizers';

// Shared GLB loader for the (currently one, more coming) hand-modelled assets in public/models/, as an
// alternative to the primitive-built craftables in craftables.ts/weaponModels.ts. One GLTFLoader (wired
// for EXT_meshopt_compression, which the export pipeline uses) shared by every caller, and each URL's
// parsed scene is cached so a deployable placed 50 times only downloads + parses once -- every instance
// after the first clones the cached template (three's Object3D.clone() deep-clones the node graph but
// shares geometry/material buffers, so cloning is cheap).

const loader = new GLTFLoader();
// Decode EXT_meshopt_compression on WORKER threads (GLTFLoader takes the decodeGltfBufferAsync path
// when workers are up). ~26 buildable GLBs (40-70k verts each) + ~30 model GLBs preload at boot; sync
// decode ran them ALL on the main thread and contributed to the world-start freeze (owner 2026-07-13).
MeshoptDecoder.useWorkers(2);
loader.setMeshoptDecoder(MeshoptDecoder);

const pending = new Map<string, Promise<THREE.Object3D>>();
const ready = new Map<string, THREE.Object3D>(); // resolved templates, for the synchronous fast path
const failed = new Set<string>(); // URLs whose load rejected (absent/broken) -- skip retries so a missing asset logs ONCE (at preload) instead of re-erroring on every onGLBReady caller

// Wait for resizersReady too, not just the GLB parse: every caller fits the template's scale via
// buildableScale() the MOMENT it resolves (craftables.ts's fitGLBTemplate, called once per template and
// never revisited). #showcase -- and any player who places a brand-new buildable in the first seconds of a
// session -- builds it at/near boot, racing this GLB fetch against the tiny resizer JSON fetch. If the GLB
// (hundreds of KB, needs meshopt decode) happened to win, the scale got permanently fit at the fallback
// even though a real saved value was seconds away. Real bug, caught 2026-07-08: furnace/large_furnace sat
// at scale 1 in-game despite 0.75/2.135 saved. The JSON is tiny, so this costs nothing in practice.
function load(url: string): Promise<THREE.Object3D> {
  let p = pending.get(url);
  if (!p) {
    p = Promise.all([loader.loadAsync(url), resizersReady]).then(([gltf]) => { ready.set(url, gltf.scene); return gltf.scene; });
    p.catch(() => failed.add(url)); // record failure on a side branch (the returned `p` still rejects for the caller's own catch)
    pending.set(url, p);
  }
  return p;
}

// Kick off the fetch+parse early (e.g. at module load of whatever file needs it), so by the time a
// player actually reaches this deployable in a session, the template is already warm.
export function preloadGLB(url: string): void {
  load(url).catch((e) => console.error('[oxide gltf] failed to load', url, e));
}

// A clone of the cached template if it has already resolved, else null. Callers use this for the
// synchronous fast path (every instance after the first, once a session is warm) and fall back to
// onGLBReady for the very first request.
export function cloneGLBIfReady(url: string): THREE.Object3D | null {
  const t = ready.get(url);
  return t ? t.clone(true) : null;
}

// Resolve with a fresh clone once the GLB is loaded (immediately, if it already is).
export function onGLBReady(url: string, cb: (clone: THREE.Object3D) => void): void {
  const t = ready.get(url);
  if (t) { cb(t.clone(true)); return; }
  if (failed.has(url)) return; // known-bad/absent asset: don't retry or re-log (preloadGLB already logged it once)
  load(url).then((scene) => cb(scene.clone(true))).catch(() => { /* recorded by load()'s side-catch + logged by preloadGLB */ });
}

// --- shared "hand-modelled GLB replacing a procedural placeholder" pattern -------------------------
// Every owner-authored asset (craftables.ts's buildables, assets.ts's world-scatter barrels) follows
// the SAME shape: show the OLD procedural model instantly (an empty group would just be invisible while
// the fetch/parse is in flight), swap to the real GLB the moment it resolves. Raw GLBs pivot at their own
// volumetric centre, not base-anchored like the primitive pieces here, so fitGLBTemplate recentres
// horizontally and rests the bottom on y=0. Scale lives in buildable_resizers.json (F4 -> OBJECTS ->
// Buildables dials it in against a real foundation/wall/player rig and saves it there); `fallbackScale`
// only seeds a fresh clone with nothing saved yet. `file` is the bare filename under public/models/ (the
// resizer JSON's key) -- callers build the '/models/' + file URL themselves.
export function fitGLBTemplate(scene: THREE.Object3D, file: string, fallbackScale: number): THREE.Object3D {
  const box = new THREE.Box3().setFromObject(scene);
  const center = new THREE.Vector3(); box.getCenter(center);
  scene.position.set(-center.x, -box.min.y, -center.z); // recentre horizontally, rest the bottom on y=0
  const wrap = new THREE.Group();
  wrap.add(scene);
  wrap.scale.setScalar(buildableScale(file, fallbackScale));
  wrap.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.castShadow = true; m.receiveShadow = true; } }); // deploy()'s traverse already ran by the time an async swap lands
  return wrap;
}
// Wall/ceiling-mounted furnishings (shelf/torch_sconce/wood_sign/...) don't floor-rest: deployRules.ts's
// MountSpec seats them by their OWN half-extents off the host face, so the fit has to centre the item on
// its own middle (X *and* Y, unlike the floor-rest convention above) with the mount face -- the side that
// meets the wall -- at local z=0 and the bulk extending toward +z. Mirrors fitGLBTemplate's grounding trick
// on the depth axis instead of the vertical one.
export function fitGLBWallMount(scene: THREE.Object3D, file: string, fallbackScale: number): THREE.Object3D {
  const box = new THREE.Box3().setFromObject(scene);
  const center = new THREE.Vector3(); box.getCenter(center);
  scene.position.set(-center.x, -center.y, -box.min.z);
  const wrap = new THREE.Group();
  wrap.add(scene);
  wrap.scale.setScalar(buildableScale(file, fallbackScale));
  wrap.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.castShadow = true; m.receiveShadow = true; } });
  return wrap;
}

// Small held tools (rock, ...) that aren't a real WeaponId so they bypass weaponModels.ts's WEAPON_GLB map
// entirely -- they sit at the group's own origin in both the FP and 3P builders (no floor-rest, no wall-mount),
// so centre on all 3 axes rather than grounding/facing like the fits above.
export function fitGLBHeld(scene: THREE.Object3D, file: string, fallbackScale: number): THREE.Object3D {
  const box = new THREE.Box3().setFromObject(scene);
  const center = new THREE.Vector3(); box.getCenter(center);
  scene.position.set(-center.x, -center.y, -center.z);
  const wrap = new THREE.Group();
  wrap.add(scene);
  wrap.scale.setScalar(buildableScale(file, fallbackScale));
  wrap.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.castShadow = true; });
  return wrap;
}
// Visit every vertex of a subtree in ROOT-LOCAL space (each mesh's chain of local matrices composed up
// to -- not including -- root). Used to auto-derive fit points from arbitrary owner GLBs: the muzzle tip
// (front-slab centroid), the iron-sight line (top of the gun), bow limb tips for the string. READ-only --
// never bakes matrices into the buffers (meshopt positions are normalized Int16; see the vault note).
// fromBufferAttribute denormalizes quantized attributes, so the numbers come out in real model units.
export function eachVertexLocal(root: THREE.Object3D, cb: (v: THREE.Vector3) => void): void {
  const rel = new THREE.Matrix4();
  const v = new THREE.Vector3();
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    rel.identity();
    let n: THREE.Object3D | null = o;
    while (n && n !== root) { n.updateMatrix(); rel.premultiply(n.matrix); n = n.parent; }
    const pos = mesh.geometry.getAttribute('position');
    if (!pos) return;
    for (let i = 0; i < pos.count; i++) { v.fromBufferAttribute(pos as THREE.BufferAttribute, i).applyMatrix4(rel); cb(v); }
  });
}

type GLBFit = (scene: THREE.Object3D, file: string, fallbackScale: number) => THREE.Object3D;
export function makeGLBBuildable(file: string, fallbackScale: number, buildFallback: (g: THREE.Group) => void, fit: GLBFit = fitGLBTemplate): THREE.Group {
  const url = '/models/' + file;
  const g = new THREE.Group();
  const readyScene = cloneGLBIfReady(url);
  if (readyScene) { g.add(fit(readyScene, file, fallbackScale)); return g; }
  buildFallback(g); // not loaded yet: the old procedural shape as an instant placeholder
  onGLBReady(url, (clone) => {
    g.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.geometry.dispose(); }); // one-off placeholder geometry, not the shared cached materials
    g.clear();
    g.scale.set(1, 1, 1); // a hero()-scaled placeholder (baseclaim/benches/locker) left 1.15 on the group -> reset so the GLB lands at its pure calibrated scale (matches F4)
    g.add(fit(clone, file, fallbackScale));
  });
  return g;
}
